import React from 'react';
import {
  IconButton, Pane, Select, Strong, Table, Text, TextInput, toaster,
  SearchIcon,
  Combobox,
  // PanelTableIcon,
  Dialog,
} from 'evergreen-ui';
import {
  format, subDays, set, isAfter, isBefore,
} from 'date-fns';
import { createUseStyles } from 'react-jss';
import ReactPaginate from 'react-paginate';

import CheckboxList from 'Shared/CheckboxList';
import { VirtualList } from 'Utils/VirtualList';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { useLazyGetUserActionsQuery } from 'Services/api/userLogsApi';
import {
  useGetUserActionTypesQuery, useGetUsersQuery,
} from 'Services/api/usersApi';
import { useSelector } from 'react-redux';
import { timeZoneSelector } from 'Features/App/appSlice';
import { userInfoSelector } from 'Services/auth.service';
import { timeZoneToUTC, UTCtoTimeZone } from 'Utils/UTCConverter';
import { JSOG } from 'Utils/JSOG';
import { DATE_FORMATS } from 'Utils/formats';
import { useGetHModelsQuery } from 'Services/api/v2/h-models';

const useStyles = createUseStyles({
  logsWrapper: {
    display: 'grid',
    height: '100%',
    columnGap: '.5em',
  },
  searchTableWrapper: {
    display: 'grid',
    width: '100%',
    gridTemplateRows: 'max-content 1fr',
    borderRight: '1px solid lightgray',
    paddingRight: '.5em',
  },

  tableStripe: {
    width: '50em',
    '& td': {
      padding: '.4em .5em .4em .5em',
    },
    '& tr:nth-child(odd)': {
      backgroundColor: 'rgba(67, 90, 111, 0.06)',
    },
    marginBottom: '.5em',
  },
  userLogsWrapper: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    height: '100%',
    rowGap: '.5em',
  },
  logsControlBar: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    columnGap: '.5em',
    justifyContent: 'center',
    alignItems: 'center',
    '& input': {
      width: 'auto',
    },
  },
  tableWrapper: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    border: '1px solid #E4E7EB',
  },
  infoBlock: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
    gap: '.2em',
  },
});

const PATH = {
  users: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users`,
  userActionTypes: '/useractiontypes',
  logs: (username, actions) => `/user-actions?userName=${username}&${actions}`,
};

export const INPUT_DATETIME = "yyyy-MM-dd'T'HH:mm";

const sizes = [10, 25, 50, 100];

export const initialParamsState = {
  // -7 дней от сегодняшней даты
  startDateUTC: format(subDays(set(new Date(), { hours: 0, minutes: 0, seconds: 0 }), 7), INPUT_DATETIME),
  endDateUTC: format(new Date(), INPUT_DATETIME),
  size: sizes[0],
};

function Logs() {
  const classes = useStyles();
  const selectedZone = useSelector(timeZoneSelector);
  const { currentUser } = useSelector(userInfoSelector);

  const [params, setParams] = React.useState(initialParamsState);

  const [isShownDialog, setIsShownDialog] = React.useState(false);
  const [info, setInfo] = React.useState({});

  const [getLogs, { data: logs, isFetching: isFetchingLogs }] = useLazyGetUserActionsQuery();
  const { data: users = [], isFetching: isFetchingUsers } = useGetUsersQuery(PATH.users);
  const {
    data: userActionsTypes = [], isFetching: isFetchingActionType, isSuccess: isSuccessUserActionTypes,
  } = useGetUserActionTypesQuery(PATH.userActionTypes);
  const { data: hModels = [], isSuccess: isSuccessHModels } = useGetHModelsQuery();

  const [userActions, setUserActions] = React.useState(null);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [filterActionTypes, setFilterActionTypes] = React.useState(null);
  const [filterHModels, setFilterHModels] = React.useState(null);
  const [hModel, setHmodel] = React.useState(null);
  const [actionId, setActionId] = React.useState('');

  const fetchLogs = React.useCallback(async ({ username, page = 0 } = {}) => {
    if (filterActionTypes && filterHModels) {
      const fetchParams = {
        ...params,
        startDateUTC: timeZoneToUTC(params.startDateUTC, selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
        endDateUTC: timeZoneToUTC(params.endDateUTC, selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
        page,
        ...(actionId && { objectPk: actionId }),
        sort: 'desc',
        actionTypes: filterActionTypes.map(({ value }) => value),
        ...((username || selectedUser) && { userName: username || selectedUser }),
        // models: filterHModels.map(({ value }) => value),
        ...(hModel && { models: hModel }),
        // userName: 'System',
      };

      const searchParams = Object.keys(fetchParams)
        .map((key) => `${encodeURI(key)}=${encodeURI(fetchParams[key])}`)
        .join('&');

      getLogs(searchParams);
    }
  }, [actionId, filterActionTypes, filterHModels, getLogs, hModel, params, selectedUser, selectedZone.selectedUserTimeZone]);

  const onChangeParams = React.useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'startDateUTC') {
      if (!isBefore(new Date(value), new Date(params.endDateUTC))) {
        toaster.danger('Дата начала не может быть больше даты окончания');
        return;
      }
    } else if (name === 'endDateUTC') {
      if (!isAfter(new Date(value), new Date(params.startDateUTC))) {
        toaster.danger('Дата окончания не может быть меньше даты начала');
        return;
      }
    }
    setParams((cur) => ({
      ...cur,
      [name]: value,
    }));
  }, [params]);

  const selectUser = React.useCallback((e) => {
    if (e !== null) {
      setSelectedUser(e?.username || '');
      // fetchLogs({ username: e?.username });
    } else setSelectedUser(null);
  }, []);

  const selectHmodel = React.useCallback((e) => {
    if (e !== null) {
      setHmodel(e.object);
    } else {
      setActionId('');
      setHmodel(null);
    }
  }, []);

  const onChangeActionId = React.useCallback((e) => {
    const { value } = e.target;
    setActionId(value);
  }, []);

  const onClickRow = React.useCallback((obj) => {
    setInfo(obj);
    setIsShownDialog(true);
  }, []);

  const onCloseComplete = React.useCallback(() => {
    setInfo({});
    setIsShownDialog(false);
  }, []);
  // render
  const LogRow = React.useCallback((item) => (
    <Table.Row
      style={{ cursor: 'pointer' }}
      onClick={() => onClickRow(item)}
    >
      <Table.TextCell><Text>{item?.id?.timeUTC ? UTCtoTimeZone(item?.id?.timeUTC, selectedZone.selectedUserTimeZone) : ''}</Text></Table.TextCell>
      <Table.TextCell><Text>{item?.id?.userName}</Text></Table.TextCell>
      <Table.TextCell><Text>{item.userActionType?.description}</Text></Table.TextCell>
      <Table.TextCell flexBasis={400} flexGrow={0} flexShrink={0}><Text>{item?.model?.name || item?.model?.object}</Text></Table.TextCell>
      <Table.TextCell><Text>{item?.id?.objectPk}</Text></Table.TextCell>
    </Table.Row>
  ), [onClickRow, selectedZone.selectedUserTimeZone]);

  const LogsList = React.useCallback(() => {
    if (isFetchingLogs) return <SuspenseSpinner />;
    if (!userActions?.content?.length) return <Table.Row><Table.Cell><Text>Нет логов</Text></Table.Cell></Table.Row>;
    return <VirtualList data={userActions?.content} row={LogRow} />;
  }, [LogRow, isFetchingLogs, userActions]);

  React.useEffect(() => {
    if (userActions === null && isSuccessUserActionTypes && isSuccessHModels) {
      fetchLogs({ username: selectedUser });
    }
  }, [fetchLogs, isSuccessHModels, isSuccessUserActionTypes, selectedUser, userActions]);

  React.useEffect(() => {
    if (isSuccessUserActionTypes) setFilterActionTypes(userActionsTypes.map(({ description, action }) => ({ label: description, value: action })));
  }, [isSuccessUserActionTypes, userActionsTypes]);

  React.useEffect(() => {
    if (isSuccessHModels) setFilterHModels(hModels.map(({ name, object }) => ({ label: name || object, value: object })));
  }, [hModels, isSuccessHModels]);

  React.useEffect(() => {
    if (logs && userActionsTypes.length > 0) setUserActions(logs);
  }, [logs, userActionsTypes]);

  React.useEffect(() => {
    if (selectedZone.selectedUserTimeZone) setUserActions(null);
  }, [selectedZone.selectedUserTimeZone]);

  React.useEffect(() => {
    if (currentUser) setSelectedUser(currentUser.username);
  }, [currentUser]);

  if (isFetchingUsers || isFetchingActionType) return <SuspenseSpinner />;
  return (
    <Pane className={classes.logsWrapper}>
      <UserActionDialog isShown={isShownDialog} onCloseComplete={onCloseComplete} info={info} selectedZone={selectedZone} />
      <Pane className={classes.userLogsWrapper}>
        <Pane className={classes.logsControlBar}>
          <Text>с</Text>
          <TextInput
            type="datetime-local"
            name="startDateUTC"
            value={params.startDateUTC}
            onChange={onChangeParams}
          />
          <Text>по</Text>
          <TextInput
            type="datetime-local"
            name="endDateUTC"
            value={params.endDateUTC}
            onChange={onChangeParams}
          />
          <Text>показывать по</Text>
          <Select
            name="size"
            value={params.size}
            onChange={onChangeParams}
          >
            {sizes.map((size) => <option key={size}>{size}</option>)}
          </Select>
          <Combobox
            // selectedItem={selectedUser}
            disabled={users?.length === 0}
            value={selectedUser}
            width="100%"
            placeholder="Выберите пользователя..."
            onChange={selectUser}
            initialSelectedItem={{ email: currentUser.emailID, ...currentUser }}
            itemToString={(u) => (u ? (u?.email ? `[${u?.email}] ${u?.username}` : `${u?.username}`) : '')}
            items={users}
          />
          <Combobox
            // selectedItem={selectedUser}
            disabled={hModels?.length === 0}
            value={hModel}
            width="100%"
            minWidth={400}
            autocompleteProps={{
              popoverMinWidth: 400,
            }}
            placeholder="Выберите объект..."
            onChange={selectHmodel}
            itemToString={(model) => (model?.name ?? model?.object) || ''}
            items={hModels}
          />
          <TextInput
            placeholder="Введите ID"
            value={actionId}
            onChange={onChangeActionId}
            // disabled={!hModel}
          />
          <CheckboxList
            tooltip="Действия"
            options={userActionsTypes.map(({ description, action }) => ({ label: description, value: action }))}
            selectedItems={filterActionTypes || []}
            setSelectedItems={setFilterActionTypes}
          />
          {/* <CheckboxList
            tooltip="Объекты"
            options={hModels.map(({ name, object }) => ({ label: name || object, value: object }))}
            selectedItems={filterHModels || []}
            setSelectedItems={setFilterHModels}
            icon={PanelTableIcon}
          /> */}
          <IconButton
            icon={SearchIcon}
            intent="success"
            onClick={fetchLogs}
            appearance="primary"
            disabled={filterActionTypes?.length === 0 || isFetchingLogs}
          />
        </Pane>
        <Pane className={classes.tableWrapper}>
          <Table>
            <Table.Head height="auto" minHeight={40}>
              <Table.HeaderCell>
                <Strong>
                  Дата UTC
                  {selectedZone.selectedUserTimeZone}
                </Strong>
              </Table.HeaderCell>
              <Table.HeaderCell><Strong>Пользователь</Strong></Table.HeaderCell>
              <Table.HeaderCell><Strong>Действие</Strong></Table.HeaderCell>
              <Table.HeaderCell flexBasis={400} flexGrow={0} flexShrink={0}><Strong>Объект</Strong></Table.HeaderCell>
              <Table.HeaderCell><Strong>ID</Strong></Table.HeaderCell>
            </Table.Head>
          </Table>
          <LogsList />
        </Pane>
        {
          userActions?.totalPages > 1
            ? (
              <ReactPaginate
                forcePage={userActions?.number || 0}
                previousLabel="пред."
                nextLabel="след."
                breakLabel="..."
                breakClassName="break-me"
                pageCount={userActions?.totalPages || 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={({ selected }) => fetchLogs({ page: selected })}
                containerClassName="pagination"
                pageLinkClassName="pageLink"
                subContainerClassName="pages pagination"
                activeClassName="active"
              />
            )
            : null
        }
      </Pane>
    </Pane>
  );
}

const UserActionDialog = React.memo(({ isShown, info, onCloseComplete, selectedZone }) => {
  const classes = useStyles();
  const object = info?.object ? JSON.parse(JSOG.decode(info.object)) : {};
  return (
    <Dialog
      isShown={isShown}
      onCloseComplete={onCloseComplete}
      confirmLabel="Готово"
      hasCancel={false}
      width="max-content"
      title="Лог пользователя"
      shouldCloseOnOverlayClick={false}
    >
      <table className={classes.tableStripe}>
        <tbody>
          <RowTableInfo label="Пользователь" text={info?.id?.userName} />
          <RowTableInfo label="Дата" text={info?.id?.timeUTC ? UTCtoTimeZone(info?.id?.timeUTC, selectedZone.selectedUserTimeZone) : ''} />
          <RowTableInfo label="Действие" text={info?.userActionType?.description} />
          <RowTableInfo label="Объект" text={info?.model?.object} />
          <RowTableInfo label="ID" text={info?.id?.objectPk} />

        </tbody>
      </table>
      <Pane className={classes.infoBlock}>
        {
          Object.keys(object).filter((key) => typeof object?.[key] !== 'object').map((key) => (
            <span key={key} style={{ borderBottom: '1px solid #E4E7EB' }}>
              <Strong>
                {key}
                :
                {' '}
              </Strong>
              <Text>{`${object[key]}`}</Text>
            </span>
          ))
        }
      </Pane>
    </Dialog>
  );
});

UserActionDialog.displayName = 'UserActionDialog';

const RowTableInfo = React.memo(({ label, text }) => (text
  ? (
    <tr>
      <td>
        <Strong>{`${label}:`}</Strong>
      </td>
      <td>
        <Text>{text}</Text>
      </td>
    </tr>
  )
  : null));
RowTableInfo.displayName = 'RowTableInfo';

export default Logs;
